import React, { useState, useEffect } from "react";

import { withStyles, createStyles, Theme } from "@material-ui/core/styles";

import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import IconDelete from "@material-ui/icons/Delete";

import DropdownEditor from "../../lib/components/DropdownEditor";
import TextEditor from "../../lib/components/TextEditor";
import { CellActionsFormatter } from "../../lib/helpers/CellActionsFormatter";
import { SelectColumn } from "react-data-grid";
import { CheckboxEditor, CheckboxFormatter } from "../../lib/components/gridhelper/CheckboxEditor";
import CustomAutoCompleteFilter from "../../lib/components/customdatagridfilter/CustomFilter";

const styles = (theme: Theme) =>
  createStyles({
    root: {
      appearance: "none",
      boxSizing: "border-box",
      width: "100%",
      height: "100%",
      padding: "0px 6px 0 6px",
      border: "2px solid #ccc",
      verticalAlign: "top",
      color: "black",
      textAlign: "right",
      fontSize: "15px",
      "&:focus": {
        borderColor: "black",
        outline: "none",
      },
      "&::placeholder": {
        color: "#999",
        opacity: 1,
      },
    },
  });

const CountEditorUnstyled = ({ row, column, counts, onRowChange, classes }) => {
  const [count, setCount] = useState(row[column.key]);
  const [dataItems, setDataItems] = useState([]);
  const [open, setOpen] = useState(true);

  useEffect(() => {
    const dataItemsList = counts.filter((item) => row.pack_id && item.pack_id == row.pack_id).sort((a, b) => a.count.localeCompare(b.count));
    setDataItems(dataItemsList);
  }, [row, counts]);

  const handleChangeComplete = (e) => {
    const selectedValues = e.target.value;
    const sorted = selectedValues.sort((a, b) => Number(a) - Number(b));

    const newValue =
      sorted.length > 1 ? sorted.join("/") : sorted[0] || "";

    setCount(newValue);
    onRowChange({ ...row, [column.key]: newValue });
  };

  return (
    <Select
      multiple
      autoFocus
      open={open}
      value={count.split("/")}
      onClose={() => setOpen(false)}
      onChange={handleChangeComplete}
      style={{ width: "100%", height: "100%", backgroundColor: "lightgray" }}
    >
      {dataItems.map((item) => (
        <MenuItem key={item.id} value={item.count}>
          {item.count}
        </MenuItem>
      ))}
    </Select>
  );
};

const CountEditor = withStyles(styles)(CountEditorUnstyled);

const DirtyFormatter = ({ row, column }) => {
  if (row) {
    const colorNew = row.isDirty && row.isDirty == 1 ? "red" : "black";
    return <div style={{ color: colorNew }}>{row[column.key]}</div>;
  }
  return <div>{row[column.key]}</div>;
};

const cellWidthSmallX = 80;
const cellWidthSmall = 115;
const cellWidthMedium = 150;

const defaultColumnProperties = {
  resizable: true,
  sortable: true,
};

export const TemplatesDetailColumns = (maintenancemode, targetsRegion, targets, counts, classesArr, coldrooms, brands, inventories, mgps, handleRemove, packs, data, filters, columnArrangement, columnsWidth) => {
  const columns: any[] = [
    {
      key: "groupline",
      name: "Line",
      editable: true,
      editor: (props) => <TextEditor {...props} />,
      formatter: DirtyFormatter,
      width: cellWidthSmallX,
      filterable: true,
      filterRenderer: (props) => (
        <CustomAutoCompleteFilter
          {...props}
          multiSelection={true}
          currentFilters={filters}
          fieldname={"groupline"}
          data={data}
          section="templatesDetail"
        />
      ),
    },
    {
      key: "volume",
      name: "Volume",
      editable: true,
      editor: (props) => <TextEditor {...props} />,
      formatter: DirtyFormatter,
      width: cellWidthMedium,
      filterable: true,
      filterRenderer: (props) => (
        <CustomAutoCompleteFilter
          {...props}
          multiSelection={true}
          currentFilters={filters}
          fieldname={"volume"}
          data={data}
          section="templatesDetail"
        />
      ),
    },
    {
      key: "region",
      name: "TR",
      editor: (props) => <DropdownEditor {...props} options={targetsRegion} valueKey="value" />,
      width: cellWidthSmallX,
      formatter: DirtyFormatter,
      filterable: true,
      filterRenderer: (props) => (
        <CustomAutoCompleteFilter
          {...props}
          multiSelection={true}
          currentFilters={filters}
          fieldname={"region"}
          data={data}
          section="templatesDetail"
        />
      ),
    },
    {
      key: "market",
      name: "TM",
      width: cellWidthSmallX,
      formatter: DirtyFormatter,
      editor: (props) => (
        <DropdownEditor
          {...props}
          valueKey="value"
          options={targets.reduce((arr, target) => {
            const { row } = props;
            if (target.region === row.region && !arr.some((r) => r.value === target.market)) {
              arr.push({ id: target.id, value: target.market });
            }
            return arr;
          }, [])}
        />
      ),
      filterable: true,
      filterRenderer: (props) => (
        <CustomAutoCompleteFilter
          {...props}
          multiSelection={true}
          currentFilters={filters}
          fieldname={"market"}
          data={data}
          section="templatesDetail"
        />
      ),
    },
    {
      key: "country",
      name: "TC",
      width: cellWidthSmallX,
      formatter: DirtyFormatter,
      editor: (props) => (
        <DropdownEditor
          {...props}
          valueKey="value"
          options={targets.reduce((arr, target) => {
            const { row } = props;
            if (target.region === row.region && target.market === row.market && !arr.some((r) => r.value === target.country)) {
              arr.push({ id: target.id, value: target.country });
            }
            return arr;
          }, [])}
        />
      ),
      filterable: true,
      filterRenderer: (props) => (
        <CustomAutoCompleteFilter
          {...props}
          multiSelection={true}
          currentFilters={filters}
          fieldname={"country"}
          data={data}
          section="templatesDetail"
        />
      ),
    },
    {
      key: "class",
      name: "Class",
      editor: (props) => <DropdownEditor {...props} options={classesArr} valueKey="code" displayKey="code" />,
      width: cellWidthSmallX,
      formatter: DirtyFormatter,
      filterable: true,
      filterRenderer: (props) => (
        <CustomAutoCompleteFilter
          {...props}
          multiSelection={true}
          currentFilters={filters}
          fieldname={"class"}
          data={data}
          section="templatesDetail"
        />
      ),
    },
    {
      key: "pack_code",
      name: "Pack",
      editor: (props) => {
        const uniquePacks = packs
          .reduce((acc, pack) => {
            if (!acc.some((p) => p.code === pack.code)) {
              acc.push({
                id: pack.id,
                code: pack.code,
                value: pack.code,
              });
            }
            return acc;
          }, [])
          .sort((a, b) => a.code.localeCompare(b.code));

        return <DropdownEditor {...props} options={uniquePacks} valueKey="code" displayKey="code" />;
      },
      width: cellWidthSmallX,
      formatter: DirtyFormatter,
      filterable: true,
      filterRenderer: (props) => (
        <CustomAutoCompleteFilter
          {...props}
          multiSelection={true}
          currentFilters={filters}
          fieldname={"pack_code"}
          data={data}
          section="templatesDetail"
        />
      ),
    },
    {
      key: "pack_carton",
      name: "Ctns",
      editor: (props) => {
        const cartonOptions = packs
          .reduce((acc, curr) => {
            const matches = curr.code === props.row.pack_code;
            if (!matches) return acc;

            if (!acc.some((item) => item.value === curr.carton)) {
              acc.push({
                id: curr.id,
                value: String(curr.carton),
                display: String(curr.carton),
              });
            }
            return acc;
          }, [])
          .sort((a, b) => Number(a.value) - Number(b.value));

        return <DropdownEditor {...props} options={cartonOptions} valueKey="value" displayKey="display" />;
      },
      width: cellWidthSmallX,
      formatter: DirtyFormatter,
      filterable: true,
      filterRenderer: (props) => (
        <CustomAutoCompleteFilter
          {...props}
          multiSelection={true}
          currentFilters={filters}
          fieldname={"pack_carton"}
          data={data}
          section="templatesDetail"
        />
      ),
    },
    {
      key: "conventional",
      name: "Conventional",
      editor: (props) => <CheckboxEditor {...props} />,
      width: cellWidthSmallX,
      formatter: CheckboxFormatter,
      filterable: true,
      filterRenderer: (props) => (
        <CustomAutoCompleteFilter
          {...props}
          multiSelection={true}
          currentFilters={filters}
          fieldname={"conventional"}
          data={data}
          section="templatesDetail"
        />
      ),
    },
    {
      key: "count",
      name: "Count",
      editor: (props) => <CountEditor {...props} counts={counts} />,
      width: cellWidthSmallX,
      formatter: DirtyFormatter,
      filterable: true,
      filterRenderer: (props) => (
        <CustomAutoCompleteFilter
          {...props}
          multiSelection={true}
          currentFilters={filters}
          fieldname={"count"}
          data={data}
          section="templatesDetail"
        />
      ),
    },
    {
      key: "countconstraint",
      name: "Constraint",
      editable: true,
      editor: (props) => <TextEditor {...props} />,
      width: cellWidthSmallX,
      formatter: DirtyFormatter,
      filterable: true,
      filterRenderer: (props) => (
        <CustomAutoCompleteFilter
          {...props}
          multiSelection={true}
          currentFilters={filters}
          fieldname={"countconstraint"}
          data={data}
          section="templatesDetail"
        />
      ),
    },
    {
      key: "coldroom",
      name: "Coldroom",
      editor: (props) => <DropdownEditor {...props} options={coldrooms} valueKey="value" displayKey="code" />,
      width: cellWidthSmall,
      formatter: DirtyFormatter,
      filterable: true,
      filterRenderer: (props) => (
        <CustomAutoCompleteFilter
          {...props}
          multiSelection={true}
          currentFilters={filters}
          fieldname={"coldroom"}
          data={data}
          section="templatesDetail"
        />
      ),
    },
    {
      key: "brand",
      name: "Brand",
      editor: (props) => <DropdownEditor {...props} options={brands} valueKey="value" displayKey="code" />,
      width: cellWidthSmallX,
      formatter: DirtyFormatter,
      filterable: true,
      filterRenderer: (props) => (
        <CustomAutoCompleteFilter
          {...props}
          multiSelection={true}
          currentFilters={filters}
          fieldname={"brand"}
          data={data}
          section="templatesDetail"
        />
      ),
    },
    {
      key: "code",
      name: "Inventory",
      editor: (props) => (
        <DropdownEditor
          {...props}
          options={inventories}
          valueKey="code"
          displayKey="code"
        />
      ),
      width: cellWidthSmall,
      formatter: DirtyFormatter,
      filterable: true,
      filterRenderer: (props) => (
        <CustomAutoCompleteFilter
          {...props}
          multiSelection={true}
          currentFilters={filters}
          fieldname={"code"}
          data={data}
          section="templatesDetail"
        />
      ),
    },
    {
      key: "mgp",
      name: "Selling Terms",
      editor: (props) => <DropdownEditor {...props} options={mgps} />,
      width: cellWidthSmallX,
      formatter: DirtyFormatter,
      filterable: true,
      filterRenderer: (props) => (
        <CustomAutoCompleteFilter
          {...props}
          multiSelection={true}
          currentFilters={filters}
          fieldname={"mgp"}
          data={data}
          section="templatesDetail"
        />
      ),
    },
  ].map((c) => ({ ...c, ...defaultColumnProperties }));

  if (!maintenancemode) {
    columns.splice(1, 0, SelectColumn);
  } else {
    columns.splice(0, 0, {
      key: "actions",
      name: "Actions",
      width: cellWidthSmallX,
      formatter: ({ row }) => {
        const actions = cellActions(row, handleRemove);
        return (
          <div style={{ display: "flex", justifyContent: "center", gap: "5px" }}>
            <CellActionsFormatter actions={actions} />
          </div>
        );
      },
    });
  }

  return columns.map((c) => ({ ...c, ...defaultColumnProperties }));
};

const cellActions = (templatedetail, handleRemove) => {
  let actions = [
    {
      icon: (
        <span title="Delete Template Line">
          <IconDelete style={{ cursor: "pointer", color: "red" }} />
        </span>
      ),
      callback: (e) => {
        handleRemove(templatedetail.id);
      },
    },
  ];
  return actions;
};
// ^^^^^ ACTIONS ^^^^^

export const TemplatesDetailSelectedColumns = () => {
  const columns = [
    { key: "templatename", name: "Template Name", width: cellWidthMedium },
    { key: "groupnum", name: "Order", width: cellWidthSmallX },
    { key: "groupline", name: "Line", width: cellWidthSmallX },
    { key: "volume", name: "Volume", width: cellWidthMedium },
    { key: "region", name: "TR", width: cellWidthSmallX },
    { key: "market", name: "TM", width: cellWidthSmallX },
    { key: "country", name: "TC", width: cellWidthSmallX },
    { key: "class", name: "Class", width: cellWidthSmallX },
    { key: "pack_code", name: "Pack", width: cellWidthSmallX },
    { key: "pack_carton", name: "Ctns", width: cellWidthSmallX },
    { key: "conventional", name: "Conventional", width: cellWidthSmallX, formatter: (props) => <CheckboxFormatter {...props} /> },
    { key: "count", name: "Count", width: cellWidthSmallX },
    { key: "countconstraint", name: "Constraint", editable: true, width: cellWidthMedium, editor: (props) => <TextEditor {...props} /> },
    { key: "coldroom", name: "Coldroom", width: cellWidthMedium },
    { key: "brand", name: "Brand", width: cellWidthMedium },
    { key: "inventory_description", name: "Inventory", width: cellWidthMedium },
    { key: "mgp", name: "Terms", width: cellWidthMedium },
  ];
  return columns;
};
